import { Pencil, Trash2, MessageSquare } from 'lucide-react';
import type { MessageQueue } from '../../types/messageQueue';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';

interface MessageQueueListProps {
  queues: MessageQueue[];
  onEdit: (queue: MessageQueue) => void;
  onDelete: (queue: MessageQueue) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function MessageQueueList({
  queues,
  onEdit,
  onDelete,
  isLoading,
  isFiltered,
}: MessageQueueListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (queues.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquare className="h-12 w-12" />}
        title={isFiltered ? 'Nenhuma fila encontrada' : 'Nenhuma fila disponível'}
        description={
          isFiltered
            ? 'Tente ajustar sua pesquisa para encontrar o que procura'
            : 'Crie sua primeira fila de mensagens para começar'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Assuntos</TableHead>
            <TableHead>Integração</TableHead>
            <TableHead>Configurações</TableHead>
            <TableHead>PABX</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {queues.map((queue: MessageQueue) => (
            <TableRow key={queue.id}>
              <TableCell>{queue.name}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {queue.subjects && queue.subjects.length > 0 ? (
                    queue.subjects.map((subject, index) => (
                      <Badge key={index} variant="secondary">
                        {subject}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              </TableCell>
              <TableCell>{queue.integration_description || '-'}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {queue.default && <Badge variant="success">Padrão</Badge>}
                  {queue.screening && <Badge variant="warning">Triagem</Badge>}
                  {queue.ia_respond && <Badge variant="info">IA Responde Auto</Badge>}
                  {queue.timeout_messages && <Badge variant="info">Msg Timeout</Badge>}
                  {queue.show_user === false && <Badge variant="error">Oculta</Badge>}
                </div>
              </TableCell>
              <TableCell>{queue.pabx_refer || '-'}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(queue)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(queue)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default MessageQueueList;
